<template>
  <div class='compete-base'>
    <header-base />
    <confirm ref='confirm'></confirm>
    <v-container>
      <h1>{{roundName}}</h1>
      <v-window v-model='windowItem'>
        <v-window-item :value='0' :key='-1'>
          <div class='compete'>
            <h2 style='color:red'>WARNING:</h2>
            <ul>
            <li><b>Make sure you are recording your solves at this point</b>. Once you click this screen the first scramble will be revealed and the time will be recorded. If we find any discrepency between your video evidence and the start and submission times, you may be disqualified.</li>
            <li><b>Enter all times including penalties</b>. That means a 10.32 + 2 should be entered as 12.32.</li>
            <li>Ensure there are no mis-scrambles, if you have a mis-scramble, that result will be counted as a DNF. You may check the scramble preview before starting inspection.</li>
            <li>Follow WCA regulations as closely as possible. Adhere to WCA inspection using a tool like <a href='https://www.cstimer.net' target='_blank'>cstimer</a> or <a href='https://cubing.net/inspection' target='_blank'>cubing.net/inspection</a>.</li>
            <li v-if="roundID.split('R')[1] !== 'qual'">Stay in contact with your competitor (eg. over discord) during the round. You will be judging your competitor.</li>
            <li v-if="roundID.split('R')[1] !== 'qual'">This is a best {{Math.trunc(numSolves/2)+1}} of {{numSolves}}. If you have already won {{Math.trunc(numSolves/2)+1}} of the {{numSolves}} solves, then you may stop and enter DNS for the final times.</li>
            <li v-if="roundName.toLowerCase().includes('fto')"><b>Make sure your scramble orientation is correct! Your top face (U) must be the lightest color and your front face (F) must be the darkest color available. On a stock FTO, this is white on U red on F.</b></li>
            <li>Make sure you upload your video evidence immediately after submitting your times.</li>
            </ul>
          </div>
        </v-window-item>
        <v-window-item :value='i+1' v-for='(scramble,i) in scrambles' :key='i'>
          <h1>Scramble {{ i+1 }}</h1>
          <scramble-form v-model='solveTimes[i]' :scramble='scramble.scramblestr' :type='scramble.type' />
        </v-window-item>
        <v-window-item :value='scrambles.length+1' :key='-2'>
          <div class='compete'>
            <h2 style='color:red'>REVIEW:</h2>
            <span v-if="roundID.split('R')[1] !== 'qual'">If you need a tiebreaker scramble, don't press SUBMIT and contact the organizers in discord.</span><br/>
            Also, reminder to <b>submit your times and then upload and link your video evidence</b>.
          </div>
          <review-table :solverData='[{solveTimes}]' :scrambles='scrambles' />
        </v-window-item>
      </v-window>
      <v-row>
        <v-col><v-btn :disabled='windowItem === 0' @click='prevItem'>PREV</v-btn></v-col>
        <v-col v-if='windowItem === 0 || windowItem !== scrambles.length+1'><v-btn @click='nextItem'>NEXT</v-btn></v-col>
        <v-col v-else><v-btn color='primary' @click='submitTimes'>SUBMIT</v-btn></v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import utils from '@/modules/utils'
import RoundsService from '@/services/TPC/RoundsService'

import HeaderBase from '@/components/TPC/Header/Base'
import ScrambleForm from '@/components/TPC/Compete/ScrambleForm'
import ReviewTable from '@/components/TPC/Shared/ReviewTable'
import Confirm from '@/components/TPC/Shared/Confirm'

export default {
  name: 'compete-base',
  components: {
    HeaderBase,
    ScrambleForm,
    ReviewTable,
    Confirm,
  },
  data () {
    return {
      windowItem: 0,
      scrambles: [],
      solveTimes: []
    }
  },
  methods: {
    async nextItem () {
      if (this.windowItem === 0 || this.windowItem < this.scrambles.length) {
        if (this.windowItem === 0) {
          let confirm = await this.$refs.confirm.open('Confirm', `<b>Did you read the warning? Once you click next, you must start solving.</b>`, { color: 'blue', width: 500, persistent: true })
          if (!confirm) return
        }
        // opened scrambles, get list of scrambles.
        let resp = await RoundsService.openedScrambles({
          roundID: this.roundID,
          solverOrder: this.solverOrder,
          scrambleNum: this.windowItem,
        })
        if (resp.data.error === 'resultSubmission') {
          alert('Error: You have already submitted results for this round.')
          return
        } else if (!resp.data.success) {
          alert(`Try again. [error: ${resp.data.error}: ${resp.data.message}]`)
          return
        }

        if (this.windowItem === 0) {
          resp = await RoundsService.getScrambles({
            roundID: this.roundID,
            solverOrder: this.solverOrder
          })
          if (resp.data.error) {
            alert(`Try again. [error: ${resp.data.error}: ${resp.data.message}]`)
            return
          }
          this.scrambles = resp.data.scrambles
          if (this.solveTimes.length !== this.scrambles.length) {
            // fill in remaining scramble (if any new scrambles were adding before refresh)
            // NOTE: this doesn't support deleting scrambles
            this.solveTimes = this.solveTimes.concat(new Array(this.scrambles.length - this.solveTimes.length).fill({time: 0}))
          }
        }
      } else if (this.windowItem === this.scrambles.length) {
        // last scramble => going to review tab
        this.solveTimes = this.solveTimes.map(v => ({time: v.time || 0, metadata: v.metadata || 'dns'}))
      }

      this.windowItem++
    },
    prevItem () {
      this.windowItem--
    },
    async submitTimes () {
      let confirm = await this.$refs.confirm.open('Submit', `<b>Are you sure? Once you submit your times, you cannot resubmit.</b>`, { color: 'blue', width: 500, persistent: true })
      if (!confirm) return

      let resp = await RoundsService.submitResults({
        roundID: this.roundID,
        solverOrder: this.solverOrder,
        solveTimes: this.solveTimes
      })
      if (!resp.data.success) {
        alert(`Try again. If it continues to fail, show the following error message to the organizers [error: ${resp.data.error}: ${resp.data.message}]`)
      } else {
        localStorage.removeItem(`compete-${this.roundID}`)
        alert('Success!')
        this.$router.push({path: '/tpc'})
      }
    }
  },
  created () {
    const solveTimes = localStorage.getItem(`compete-${this.roundID}`)
    if (solveTimes !== null) {
      this.solveTimes = JSON.parse(solveTimes)
    }
  },
  watch: {
    windowItem () {
      localStorage.setItem(`compete-${this.roundID}`, JSON.stringify(this.solveTimes))
    }
  },
  computed: {
    roundID () {
      return this.$route.params.rid
    },
    solverOrder () {
      return this.$route.query.solverOrder !== undefined ? this.$route.query.solverOrder : -1
    },
    roundName () {
      return this.$store.state.eventName ? `${this.$store.state.eventName} ${utils.roundNameSuffix(this.roundID)}` : 'Round ' + this.roundID
    },
    isRoundBLD () {
      return this.roundName.toLowerCase().includes('bld')
    },
    numSolves () {
      return this.scrambles.length || (this.isRoundBLD ? 3 : 5)
    }
  }
}
</script>

<style scoped>
.v-window {
  min-height: 500px !important;
}

.compete {
  text-align: left;
  width: 50%;
  margin: auto;
  color: rgba(0,0,0,0.9)
}
</style>
